import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { Field, NativeSelect } from '@/components/form';
import { useOrgId } from '@/lib/org';
import {
  saveTags,
  useProjects,
  useTagGroups,
  useTrackingSettings,
  useTree,
  type TrackingValue,
  type TreeItem,
  type TreeKind,
} from '@/lib/tracking';
import { cn } from '@/lib/utils';

/**
 * Tracking on transaction forms (document 06 L4): the header pickers (location, class for the
 * whole transaction, project, tags) and the line pickers (class per row, project per row).
 * Each shows only when the company uses it; a value saved before a list item was made inactive
 * stays visible on that document.
 */

/** Two no-break spaces: native options cannot be styled, so sub-items indent with text. */
const INDENT = '\u00a0\u00a0';

function TreeSelect({
  kind,
  value,
  onChange,
  ariaLabel,
  id,
  disabled,
  className,
}: {
  kind: TreeKind;
  value: string | null;
  onChange: (id: string | null) => void;
  ariaLabel: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  const all = useTree(kind, 'all');
  const items = (all.data ?? []).filter((x: TreeItem) => x.active || x.id === value);
  return (
    <NativeSelect
      id={id}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="" />
      {items.map((x) => (
        <option key={x.id} value={x.id}>
          {x.parentId ? `${INDENT}${x.fullName}` : x.fullName}
          {x.active ? '' : ' (inactive)'}
        </option>
      ))}
    </NativeSelect>
  );
}

export function ClassSelect(props: {
  value: string | null;
  onChange: (id: string | null) => void;
  ariaLabel: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  return <TreeSelect kind="classes" {...props} />;
}

export function LocationSelect(props: {
  value: string | null;
  onChange: (id: string | null) => void;
  ariaLabel: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  return <TreeSelect kind="locations" {...props} />;
}

/** Projects of a customer (or all open projects), as QBO's Customer/project picker narrows. */
export function ProjectSelect({
  value,
  onChange,
  ariaLabel,
  id,
  customerId,
  disabled,
  className,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  ariaLabel: string;
  id?: string;
  customerId?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const projects = useProjects();
  const items = (projects.data ?? []).filter(
    (p) =>
      p.id === value || (p.status !== 'cancelled' && (!customerId || p.customerId === customerId)),
  );
  return (
    <NativeSelect
      id={id}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="" />
      {items.map((p) => (
        <option key={p.id} value={p.id}>
          {customerId ? p.name : `${p.customerName}:${p.name}`}
        </option>
      ))}
    </NativeSelect>
  );
}

/** Tags as removable chips and a grouped picker to add one (QBO tags field). */
export function TagsInput({
  value,
  onChange,
  disabled,
  id,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  id?: string;
}) {
  const groups = useTagGroups('all');
  const all = [
    ...(groups.data?.groups.flatMap((g) => g.tags) ?? []),
    ...(groups.data?.ungrouped ?? []),
  ];
  const byId = new Map(all.map((t) => [t.id, t]));
  const free = (tags: typeof all) => tags.filter((t) => t.active && !value.includes(t.id));
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="tags-input">
      {value.map((tid) => {
        const t = byId.get(tid);
        return (
          <span
            key={tid}
            className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs"
          >
            {t ? (t.groupName ? `${t.groupName}: ${t.name}` : t.name) : '…'}
            {!disabled ? (
              <button
                type="button"
                aria-label={`Remove tag ${t?.name ?? ''}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(value.filter((x) => x !== tid))}
              >
                <X className="size-3" />
              </button>
            ) : null}
          </span>
        );
      })}
      {!disabled ? (
        <NativeSelect
          id={id}
          aria-label="Add a tag"
          className="h-8 w-44"
          value=""
          onChange={(e) => e.target.value && onChange([...value, e.target.value])}
        >
          <option value="">{value.length ? 'Add another tag' : 'Add a tag'}</option>
          {groups.data?.groups.map((g) =>
            free(g.tags).length ? (
              <optgroup key={g.id} label={g.name}>
                {free(g.tags).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </optgroup>
            ) : null,
          )}
          {free(groups.data?.ungrouped ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </NativeSelect>
      ) : !value.length ? (
        <span className="text-sm text-muted-foreground">No tags</span>
      ) : null}
    </div>
  );
}

/**
 * Header fields of a transaction form: Location (one per transaction), Class (when classes are
 * one per transaction), Project (sales forms; purchase forms put it on lines) and Tags.
 */
export function TrackingHeaderFields({
  value,
  onChange,
  disabled,
  customerId,
  withProject = true,
  className,
}: {
  value: TrackingValue;
  onChange: (v: TrackingValue) => void;
  disabled?: boolean;
  customerId?: string | null;
  withProject?: boolean;
  className?: string;
}) {
  const s = useTrackingSettings();
  const set = (patch: Partial<TrackingValue>) => onChange({ ...value, ...patch });
  return (
    <div
      className={cn('grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4', className)}
      data-testid="tracking-fields"
    >
      {s.locations ? (
        <Field label={s.locationLabel}>
          {(id) => (
            <LocationSelect
              id={id}
              ariaLabel={s.locationLabel}
              disabled={disabled}
              value={value.locationId}
              onChange={(locationId) => set({ locationId })}
            />
          )}
        </Field>
      ) : null}
      {s.classes === 'per_transaction' ? (
        <Field label="Class">
          {(id) => (
            <ClassSelect
              id={id}
              ariaLabel="Class"
              disabled={disabled}
              value={value.classId}
              onChange={(classId) => set({ classId })}
            />
          )}
        </Field>
      ) : null}
      {s.projects && withProject ? (
        <Field label="Project">
          {(id) => (
            <ProjectSelect
              id={id}
              ariaLabel="Project"
              disabled={disabled}
              customerId={customerId ?? null}
              value={value.projectId}
              onChange={(projectId) => set({ projectId })}
            />
          )}
        </Field>
      ) : null}
      <Field label="Tags" className="col-span-full">
        {(id) => (
          <TagsInput
            id={id}
            disabled={disabled}
            value={value.tagIds}
            onChange={(tagIds) => set({ tagIds })}
          />
        )}
      </Field>
    </div>
  );
}

/**
 * Tags of a posted transaction, saved as they change (tags never touch the ledger, so no
 * repost; document 06 L3). Read-only for client users.
 */
export function TagsEditor({
  target,
  initial,
  canChange,
}: {
  target: { kind: 'document' | 'journal_entry' | 'payment'; id: string };
  initial: string[];
  canChange: boolean;
}) {
  const orgId = useOrgId();
  const [ids, setIds] = useState(initial);
  const save = useMutation({
    mutationFn: (next: string[]) => saveTags(orgId!, target, next),
    onSuccess: (tags) => setIds(tags.map((t) => t.id)),
  });
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-muted-foreground">Tags</span>
      <TagsInput
        value={ids}
        disabled={!canChange || save.isPending}
        onChange={(next) => {
          setIds(next);
          save.mutate(next);
        }}
      />
      {save.error ? <p className="text-xs text-destructive">{save.error.message}</p> : null}
    </div>
  );
}
